import { useEffect, useRef, useState } from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useIsTruncated } from 'hooks/useIsTruncated';
import { useCrmPricingEmbedFlag } from 'hooks/useCrmPricingEmbedFlag';
import type { VariableNode } from 'services/entities/VariablesEntity';
import { useTranslation } from 'react-i18next';
import { AccordionDetailsStyled } from '../SidePanel/component/SidePanelAccordion';
import { useActiveEditor } from '../GridDndEditor/TiptapEditor/ActiveEditorContext';
import { useAppSelector } from '../grid/reduxStore/Store';
import { getDisplayVariable } from '../GridDndEditor/TiptapEditor/extensions/variables/utils';
import { selectContentType } from '../grid/reduxStore/documentSettingsSlice';
import { useInsertArrayVariableAsTable } from '../GridDndEditor/Block/Table/crmArrayPaste/useInsertArrayVariableAsTable';
import { useCopyVariableToClipboard } from './useCopyVariableToClipboard';
import { isIntegrationsFolderPath, toArrayClipboardNode } from './utils';
import { CopyButton } from './CopyButton';

const AccordionDetailsStyledComponent = styled(AccordionDetailsStyled)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  alignSelf: 'stretch',
}));

export const TypographyStyled = styled(Typography)(({ isRounded }: { isRounded: boolean }) => ({
  borderRadius: isRounded ? '0' : '8px',
  padding: !isRounded ? '8px 7px 8px 4px' : '8px 6px 8px 20px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  color: 'rgba(0, 40, 58, 0.87)',

  '&:hover': {
    '.copyBtn': {
      opacity: 1,
      maxWidth: '60px',
      backgroundColor: 'transparent',
    },
  },
  '.MuiAutocomplete-option': {
    padding: '8px 7px 8px 4px',
  },
}));

type AccordionDetailsProps = {
  isEditor?: boolean;
  variableMeta: VariableNode;
};

export const AccordionDetails = ({ isEditor = true, variableMeta }: AccordionDetailsProps) => {
  const { t } = useTranslation();
  const { activeEditor: editor } = useActiveEditor();
  const [isCopied, setIsCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const crmPricingEmbedEnabled = useCrmPricingEmbedFlag();
  const copyVariable = useCopyVariableToClipboard();
  const insertArrayVariableAsTable = useInsertArrayVariableAsTable();

  const contentType = useAppSelector(selectContentType);
  const { tooltipTitle, displayedTitle, name, adjustedName, friendlyName } = getDisplayVariable(variableMeta, contentType);
  const { ref, isTruncated } = useIsTruncated([displayedTitle]);

  const isArrayEntry = crmPricingEmbedEnabled && variableMeta.shape?.type === 'array';
  const isIntegrationEntry = crmPricingEmbedEnabled && Boolean(variableMeta.path) && isIntegrationsFolderPath(variableMeta.path);
  const showCopyButton = !isEditor || (isIntegrationEntry && !isEditor);

  useEffect(() => () => clearTimeout(copyResetTimeoutRef.current), []);

  const handleInsertVariable = () => {
    if (!editor || !isEditor) return;
    const { from, to } = editor.state.selection;
    if (variableMeta.shape?.type === 'array') {
      const onArrayVariableInserted = editor.storage.variable?.onArrayVariableInserted;
      if (!onArrayVariableInserted) {
        insertArrayVariableAsTable(variableMeta);
        return;
      }
      const needsSpace = editor.state.doc.textBetween(to, to + 1) !== ' ';
      onArrayVariableInserted(variableMeta, { from: from - 1, to }, needsSpace);
      return;
    }
    editor.commands.insertVariable(
      { name, friendlyName, objectId: variableMeta.objectId, path: variableMeta.path ?? null, template: variableMeta.template ?? null },
      { from: from - 1, to }
    );
  };

  const handleCopy = async () => {
    const payload = isArrayEntry ? toArrayClipboardNode(variableMeta) : variableMeta;
    const success = await copyVariable(payload);
    if (success) {
      setIsCopied(true);
      copyResetTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  };

  return (
    <Tooltip
      slotProps={{ popper: { disablePortal: true } }}
      data-testid="accordion-details-test"
      title={tooltipTitle}
      enterDelay={700}
      disableHoverListener={!isTruncated}
    >
      <AccordionDetailsStyledComponent key={`${variableMeta.path}-accordian-component`} id={`${variableMeta.path}-accordian-component`}>
        <TypographyStyled
          data-testid="accordion-details-title"
          isRounded={isEditor}
          variant="body2"
          onClick={() => {
            if (isEditor) handleInsertVariable();
            else handleCopy();
          }}
        >
          <Box
            id={`${variableMeta.path}-container-box`}
            component="span"
            ref={ref}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}
          >
            {friendlyName} ·&nbsp;
            <Box component="span" sx={{ color: 'rgba(0, 40, 58, 0.60)' }}>
              {adjustedName}
            </Box>
          </Box>
          {isArrayEntry && (
            <Chip
              data-testid="variable-array-badge"
              label={t('editor.variables.array_badge')}
              size="small"
              variant="outlined"
              component="span"
              sx={{ flexShrink: 0 }}
            />
          )}
          {showCopyButton && (
            <CopyButton
              data-testid="copy-variable-btn"
              isCopied={isCopied}
              copyLabel={t('editor.variables.copy')}
              copiedLabel={t('editor.variables.copied')}
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
            />
          )}
        </TypographyStyled>
      </AccordionDetailsStyledComponent>
    </Tooltip>
  );
};
